import { FC, memo } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Bar, BarChart, CartesianGrid, Rectangle, ResponsiveContainer,
  Tooltip, XAxis, YAxis,
} from 'recharts';
import { useTheme } from 'styled-components';

import { CardErrorState } from '@/components';
import { useIsMobile } from '@/hooks';

import { chartColors } from '../../../config/colors';
import { EventKey } from '../../../views/Dashboard/queries';
import { ChartLoadingIcon } from '../../Loaders';
import { EmptyState } from '../EmptyState';

type ChartRow = {
    name: string;
} & Partial<Record<EventKey | 'total', number>>;

interface BarChartProps {
    chartData: ChartRow[];
    isLoading: boolean;
    error?: any;
    onRetry?: () => void;
}

const LABEL = 'Start Sesion';

const CustomTooltip: FC<any> = ({ active, payload, isDark }) => {
  if (active && payload && payload.length) {
    return (
      <div
        className="backdrop-blur-sm px-4 py-3 rounded-md shadow-lg border"
        style={{
          background: isDark ? 'rgba(36, 42, 51, 0.95)' : 'rgba(255, 255, 255, 0.95)',
          borderColor: isDark ? '#374151' : '#e5e7eb',
        }}
      >
        <p className="text-sm font-light" style={{ color: isDark ? '#f8fafc' : '#111827' }}>{payload[0].payload.name}</p>
        <p style={{ color: chartColors.primary }} className="text-sm font-normal">
          {LABEL}
          :
          {' '}
          {payload[0].value?.toLocaleString()}
        </p>
      </div>
    );
  }
  return null;
};

const HsBarChartComponent: FC<BarChartProps> = ({ chartData, isLoading, error, onRetry }) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const isDark = theme.mode === 'dark';
  const isMobile = useIsMobile();
  const hasData = chartData && chartData.length > 0;

  const hasValues = hasData && chartData.some(
    (item) => item.WELCOME_SCREEN_START_BUTTON_TAPPED && item.WELCOME_SCREEN_START_BUTTON_TAPPED > 0,
  );

  const gridColor = isDark ? '#374151' : '#e5e7eb';
  const axisColor = isDark ? '#9ca3af' : '#9ca3af';

  const chartHeight = isMobile ? 250 : 400;
  const chartMargin = isMobile
    ? { top: 10, right: 10, left: -20, bottom: 10 }
    : { top: 20, right: 30, left: 0, bottom: 20 };

  if (isLoading) {
    return (
      <div
        className="w-full flex flex-col items-center justify-center rounded-md"
        style={{
          height: isMobile ? '300px' : '445px',
          background: theme.colors.chart.background,
          borderColor: isDark ? '#374151' : 'rgba(226, 232, 240, 0.5)',
        }}
      >
        <ChartLoadingIcon size={isMobile ? 'sm' : 'lg'} />
        <p className="mt-4 text-xs sm:text-sm" style={{ color: isDark ? '#9ca3af' : '#6b7280' }}>{t('dashboard.loadingChartData')}</p>
      </div>
    );
  }

  if (error) {
    return (
      <div
        className="w-full flex items-center justify-center rounded-md border"
        style={{
          height: isMobile ? '300px' : '445px',
          background: theme.colors.chart.background,
          borderColor: isDark ? '#374151' : 'rgba(226, 232, 240, 0.5)',
        }}
      >
        <CardErrorState
          title={t('dashboard.chartError', 'Nie udało się wczytać wykresu')}
          error={error}
          onRetry={onRetry}
        />
      </div>
    );
  }

  return (
    <div
      className="w-full h-full rounded-md p-3 sm:p-6 shadow-sm border"
      style={{
        background: theme.colors.chart.background,
        borderColor: isDark ? '#374151' : 'rgba(226, 232, 240, 0.5)',
      }}
    >
      {!hasData || !hasValues ? (
        <EmptyState />
      ) : (
        <ResponsiveContainer width="100%" height={chartHeight}>
          <BarChart
            data={chartData}
            margin={chartMargin}
          >
            <defs>
              <linearGradient id="barGradient" x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor={chartColors.primary} stopOpacity={1} />
                <stop offset="100%" stopColor={chartColors.primaryDark} stopOpacity={0.85} />
              </linearGradient>
              <filter id="barShadow" x="-50%" y="-50%" width="200%" height="200%">
                <feDropShadow dx="0" dy="3" stdDeviation="4" floodColor={chartColors.shadow.primary} floodOpacity={chartColors.shadow.opacity} />
              </filter>
            </defs>
            <CartesianGrid
              strokeDasharray="3 3"
              stroke={gridColor}
              vertical={false}
              style={{ opacity: 0.5 }}
            />
            <XAxis
              dataKey="name"
              stroke={axisColor}
              style={{ fontSize: isMobile ? '10px' : '12px', fontWeight: 500 }}
              axisLine={{ stroke: gridColor }}
              tickLine={{ stroke: gridColor }}
              height={chartData.length > 14 ? 12 : (isMobile ? 40 : 60)}
              padding={{ left: isMobile ? 5 : 20, right: isMobile ? 5 : 20 }}
              tick={chartData.length > 14 ? false : undefined}
              interval={chartData.length > 30 ? 'preserveStartEnd' : (isMobile ? 1 : 0)}
            />
            <YAxis
              stroke={axisColor}
              style={{ fontSize: isMobile ? '10px' : '12px', fontWeight: 500 }}
              axisLine={{ stroke: gridColor }}
              tickLine={{ stroke: gridColor }}
              width={isMobile ? 30 : 60}
            />
            <Tooltip content={<CustomTooltip isDark={isDark} />} cursor={{ fill: chartColors.primary, opacity: 0.1 }} />
            <Bar
              dataKey="WELCOME_SCREEN_START_BUTTON_TAPPED"
              barSize={isMobile ? 30 : 60}
              fill="url(#barGradient)"
              activeBar={(
                <Rectangle
                  fill={chartColors.primary}
                  stroke={chartColors.primaryDark}
                  strokeWidth={2}
                  filter="url(#barShadow)"
                />
                            )}
              isAnimationActive={false}
              animationDuration={800}
              radius={[8, 8, 0, 0]}
            />
          </BarChart>
        </ResponsiveContainer>
      )}
    </div>
  );
};

// ✅ Memo z custom comparison
export const HsBarChart = memo(HsBarChartComponent, (prevProps, nextProps) => (
  prevProps.isLoading === nextProps.isLoading
        && prevProps.chartData === nextProps.chartData
));
